import { motion } from "motion/react";
import { Button } from "../ui/button";
import {
    ArrowLeft, Home, Search, FileQuestion,
    Mail, MessageCircle, Compass,
} from "lucide-react";
import { Link } from "react-router-dom";


const NotFoundPage = ({ setCurrentPage }: any) => {
    const popularPages = [
        {
            name: "Our Services",
            description: "Explore our comprehensive software development solutions",
            link: "/services",
            icon: <Compass className="w-5 h-5" />
        },
        // {
        //     name: "Portfolio",
        //     description: "View our successful project implementations",
        //     link: "/portfolio",
        //     icon: <Search className="w-5 h-5" />
        // },
        {
            name: "About Us",
            description: "Learn more about MetaFox Tech and our team",
            link: "/about",
            icon: <FileQuestion className="w-5 h-5" />
        },
        {
            name: "Contact",
            description: "Get in touch with our expert team",
            link: "/contact",
            icon: <Mail className="w-5 h-5" />
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            {/* Hero Section */}
            <section className="px-6 pt-24 pb-12 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-16 items-center min-h-[70vh]">
                    {/* Content Section */}
                    <motion.div
                        className="space-y-8"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >

                        {/* Main Heading */}
                        <div className="space-y-6 flex items-center justify-center">
                            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                                <motion.span
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                    className="block"
                                >
                                    Oops! Page
                                </motion.span>
                                <motion.span
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.8 }}
                                    className="block text-transparent bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text"
                                >
                                    Not Found
                                </motion.span>
                            </h1>
                        </div>

                        <motion.div
                            className="space-y-6 text-center"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.0 }}
                        >
                            <p className="text-xl text-slate-600 leading-relaxed max-w-7xl">
                                The page you're looking for seems to have wandered off into the digital cosmos. <br />
                                Don't worry - even the best explorers sometimes need to recalibrate their route.
                            </p>

                            <p className="text-lg text-slate-500">
                                Let's get you back on track to discover our innovative software solutions.
                            </p>
                        </motion.div>

                        {/* Action Buttons */}
                        <motion.div
                            className="flex flex-col justify-center sm:flex-row gap-4 pt-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.2 }}
                        >
                            <Link to={'/'}>
                                <Button
                                    size="lg"
                                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                                >
                                    <Home className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                                    Back to Home
                                </Button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Popular Pages Section */}
            <section className="px-6 py-1 max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">
                        Popular Destinations
                    </h2>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        While you're here, why not explore some of our most popular pages?
                        Each one is crafted to showcase our expertise and help you discover the perfect solution.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {popularPages.map((page, index) => (
                        <motion.div
                            key={page.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="group cursor-pointer"
                        >
                            <Link to={page.link}>
                                <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-blue-100 group-hover:bg-blue-500 rounded-xl flex items-center justify-center transition-colors duration-300">
                                        <div className="text-blue-600 group-hover:text-white transition-colors duration-300">
                                            {page.icon}
                                        </div>
                                    </div>
                                    <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                                        {page.name}
                                    </h3>
                                </div>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    {page.description}
                                </p>
                            </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Contact Support Section */}
            <section className="px-6 py-16 max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-3xl p-12 text-white text-center"
                >
                    <h2 className="text-3xl font-bold mb-4">
                        Still Can't Find What You're Looking For?
                    </h2>
                    <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
                        Our expert team is standing by to help you navigate to the right solution.
                        We're here to ensure your journey with MetaFox Tech is seamless and successful.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <Link to={'mailto:info@metafoxtech.com'}>
                            <Button
                                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                                <Mail className="w-5 h-5 mr-2" />
                                Contact Support
                            </Button>
                        </Link>

                        {/* <Button
                            variant="outline"
                            className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-3 text-lg backdrop-blur-sm"
                        >
                            <MessageCircle className="w-5 h-5 mr-2" />
                            Live Chat
                        </Button> */}
                    </div>

                    {/* Quick Contact Info */}
                    <div className="flex items-center justify-center gap-8 mt-8 pt-8 border-t border-white/20">
                        <div className="text-center">
                            <div className="text-lg font-semibold text-white">Email Support</div>
                            <div className="text-sm text-blue-200">Available 24/7</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-semibold text-white">Phone Support</div>
                            <div className="text-sm text-blue-200">Mon-Fri 9AM-6PM</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-semibold text-white">Live Chat</div>
                            <div className="text-sm text-blue-200">Instant Response</div>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
export default NotFoundPage;